import type { ColumnDef } from '@tanstack/react-table';

import { ExpiryDateRecordState, useGetExpiryDateRecordsForSiteQuery } from 'data/__generated__';

import { DataTable, textColumn, numberColumn, dateColumn, multiSelectColumn } from 'components/DataTable';
import { formatDateForDisplay } from 'helpers/dateTimes';
import { Loader } from 'components/Loader';
import { StatusTag } from 'pages/ExpiryDateRecords/components/StatusTag';
import { EXPIRY_DATE_RECORD_STATES_LABELS } from 'pages/ExpiryDateRecords/constants/states';

type ExpiryDateRecordRow = {
    _id: string;
    productName: string;
    expiryDate: string;
    quantityAtInventory: number;
    state: ExpiryDateRecordState;
    updatedAt: number;
    createdAt: number;
};

type ExpiryDateRecordFilter = {
    searchValue: string;
    state: ExpiryDateRecordState[];
};

const columns: ColumnDef<ExpiryDateRecordRow, unknown>[] = [
    textColumn<ExpiryDateRecordRow, ExpiryDateRecordFilter>({
        accessorKey: 'productName',
        header: 'Nom du produit',
        defaultVisible: true,
    }),
    textColumn<ExpiryDateRecordRow, ExpiryDateRecordFilter>({
        accessorKey: 'expiryDate',
        header: 'DLC',
        defaultVisible: true,
        cell: ({ row }) => <span>{formatDateForDisplay(row.original.expiryDate)}</span>,
    }),
    numberColumn<ExpiryDateRecordRow, ExpiryDateRecordFilter>({
        accessorKey: 'quantityAtInventory',
        header: 'Qt dernier inventaire',
        defaultVisible: true,
    }),
    multiSelectColumn<ExpiryDateRecordRow, ExpiryDateRecordFilter>({
        accessorKey: 'state',
        header: 'Statut',
        filterKey: 'state',
        defaultVisible: true,
        options: Object.values(ExpiryDateRecordState).map((state) => ({
            value: state,
            label: EXPIRY_DATE_RECORD_STATES_LABELS[state],
        })),
        renderOption: (option) => <StatusTag state={option.value as ExpiryDateRecordState} />,
        cell: ({ row }) => <StatusTag state={row.original.state} />,
    }),
    dateColumn<ExpiryDateRecordRow, ExpiryDateRecordFilter>({
        accessorKey: 'updatedAt',
        header: 'Date de mise à jour',
        defaultVisible: true,
    }),
    dateColumn<ExpiryDateRecordRow, ExpiryDateRecordFilter>({
        accessorKey: 'createdAt',
        header: 'Date de création',
        defaultVisible: true,
    }),
];

export const ExpiryDateRecordList = ({ columnId }: { columnId: string }) => {
    const {
        loading: expiryDateRecordsLoading,
        data: expiryDateRecordsData,
        error: expiryDateRecordsError,
    } = useGetExpiryDateRecordsForSiteQuery({ variables: { columnId } });

    if (expiryDateRecordsLoading && !expiryDateRecordsData) {
        return <Loader />;
    }

    if (expiryDateRecordsError || !expiryDateRecordsData) {
        throw new Error('Une erreur est survenue lors de la récupération des DLC');
    }

    const { getExpiryDateRecordsForSiteQuery: expiryDateRecords } = expiryDateRecordsData;

    const rows: ExpiryDateRecordRow[] = expiryDateRecords.map(
        ({ _id, createdAt, expiryDate, product: { brand, name, volume }, quantityAtInventory, state, updatedAt }) => ({
            _id,
            productName: `${brand} - ${name} - ${volume}`,
            expiryDate,
            quantityAtInventory,
            state,
            updatedAt,
            createdAt,
        }),
    );

    return (
        <DataTable<ExpiryDateRecordRow, ExpiryDateRecordFilter>
            columns={columns}
            data={rows}
            filterMode="client"
            getRowId={(row) => row._id}
            searchPlaceholder="Nom du produit"
            searchFilterKey="searchValue"
        />
    );
};
